const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Search products in the catalog
 * @param {Object} params - Search parameters
 * @param {string} params.keyword - Search keyword
 * @param {string} params.category - Product category
 * @param {string} params.vendor - Brand/vendor name
 * @param {number} params.pageNumber - Page number (1-indexed)
 * @param {number} params.pageSize - Items per page
 * @returns {Promise<Object>} Search results with products and pagination
 */
export async function searchProducts({ keyword, category, vendor, pageNumber = 1, pageSize = 25 } = {}) {
    const params = new URLSearchParams();

    if (keyword) params.append('keyword', keyword);
    if (category) params.append('category', category);
    if (vendor) params.append('vendor', vendor);
    params.append('pageNumber', pageNumber.toString());
    params.append('pageSize', pageSize.toString());

    const response = await fetch(`${API_BASE_URL}/products/search?${params.toString()}`);

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to search products');
    }

    const result = await response.json();

    if (!result.success) {
        throw new Error(result.error || 'Failed to search products');
    }

    return result.data;
}

/**
 * Get price and availability for specific SKUs
 * @param {string[]} skus - Array of Ingram part numbers
 * @returns {Promise<Object>} Map of SKU to price/availability info
 */
export async function getPriceAndAvailability(skus) {
    if (!skus || skus.length === 0) {
        return {};
    }

    const response = await fetch(`${API_BASE_URL}/products/price`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ skus })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get price and availability');
    }

    const result = await response.json();

    if (!result.success) {
        throw new Error(result.error || 'Failed to get price and availability');
    }

    return result.data;
}

/**
 * Get catalog-wide facets (categories + brands with counts).
 * Backed by the /api/products/facets endpoint which aggregates across a
 * ~2000-product sample and caches for 6 hours. Use this to populate the
 * sidebar filters instead of per-page aggregation.
 * @returns {Promise<{categories: Array, brands: Array}>}
 */
export async function getFacets() {
    const response = await fetch(`${API_BASE_URL}/products/facets`);
    if (!response.ok) {
        throw new Error('Failed to fetch facets');
    }
    const result = await response.json();
    if (!result.success) {
        throw new Error(result.error || 'Failed to fetch facets');
    }
    return result.data;
}

/**
 * Get product by ID
 * @param {string} productId - The product ID (Ingram part number)
 * @returns {Promise<Object>} Product details
 */
export async function getProductById(productId) {
    const response = await fetch(`${API_BASE_URL}/products/${encodeURIComponent(productId)}`);

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch product details');
    }

    const result = await response.json();

    if (!result.success) {
        throw new Error(result.error || 'Failed to fetch product details');
    }

    return result.data;
}

/**
 * Check API health
 * @returns {Promise<Object>} Health status
 */
export async function checkHealth() {
    const response = await fetch(`${API_BASE_URL}/products/health`);

    if (!response.ok) {
        throw new Error('API health check failed');
    }

    return response.json();
}
